import { useEffect, useRef } from 'react';
import { systemBus, EVENTS } from '../services/systemBus';
// [v1.0]: Use contentResolver for unified SRD + custom race lookup
import { getRaceData } from '../utils/contentResolver';
// [BATCH 8 / P1 D-13 — APR 2026]: Race-transition diff helper. Pre-fix
//   the merge here was purely additive — switching from Dwarf to Tiefling
//   left every Dwarven contribution attached.
import { applyRaceTransition } from '../utils/raceTransitions';

/**
 * [PURPOSE]: Automatically apply racial modifiers when race/subrace changes
 * [WHY]: Ensure character speed, darkvision, size, languages, resistances, and
 *   proficiencies stay synchronized with race selection
 * [INTEGRATION]: Used in App.jsx to watch playerStats.basics.race and playerStats.basics.subrace
 * [v1.0]: Extended to apply ALL custom race fields (damageResistance, grantedSpells,
 *   features, proficiencies) — previously only applied speed/size/darkvision/languages
 *
 * @param {Object} character - Full character object with basics.race and basics.subrace
 * @param {Function} setCharacter - State setter to update character
 * @param {Function} logActivity - Activity logging function
 */
export const useRaceEffects = (character, setCharacter, logActivity) => {
  // Track previous race to detect changes
  const previousRaceRef = useRef(null);
  const previousSubraceRef = useRef(null);

  useEffect(() => {
    // Get current race and subrace
    const currentRace = character?.basics?.race || character?.race;
    const currentSubrace = character?.basics?.subrace || character?.subrace;

    // Skip if no race set (new character)
    if (!currentRace) {
      previousRaceRef.current = currentRace;
      previousSubraceRef.current = currentSubrace;
      return;
    }

    // Detect if race or subrace changed
    const raceChanged = previousRaceRef.current && previousRaceRef.current !== currentRace;
    const subraceChanged = previousSubraceRef.current !== currentSubrace;

    // Only apply effects if race actually changed (not on initial mount)
    if (!raceChanged && !subraceChanged) {
      previousRaceRef.current = currentRace;
      previousSubraceRef.current = currentSubrace;
      return;
    }

    // [FEATURE]: Apply racial modifiers automatically
    // [v1.0]: Use contentResolver getRaceData() — checks SRD then custom sources
    // Subrace merging is handled by contentResolver.getRaceData(name, subrace)
    const raceData = getRaceData(currentRace, currentSubrace);

    if (!raceData) {
      console.warn(`Race "${currentRace}" not found in SRD or custom sources`);
      previousRaceRef.current = currentRace;
      previousSubraceRef.current = currentSubrace;
      return;
    }

    // [BATCH 8 / P1 D-13 — APR 2026]: Look up the previous race's data so
    //   applyRaceTransition can subtract its contributions (resistances,
    //   languages, features, proficiencies). Pre-fix this teardown step
    //   was missing — switching races left the prior race's data
    //   permanently attached.
    const prevRaceData = previousRaceRef.current
      ? getRaceData(previousRaceRef.current, previousSubraceRef.current) || null
      : null;

    // Tag the new race data with its name so applyRaceTransition can
    // stamp `source: <raceName>` onto added features (parity with the
    // existing convention here and in handleRaceChange).
    const newRaceDataTagged = { ...raceData, _raceName: currentRace };

    // Apply updates to character
    setCharacter(prev => {
      const delta = applyRaceTransition(prev, prevRaceData, newRaceDataTagged);

      // hitPointBonus + savingThrows aren't covered by the helper (kept
      // shape-stable for now — these aren't list contributions but
      // scalar/object replacements). Mirror the previous logic.
      const updates = { ...delta };
      if (raceData.hitPointBonus) {
        updates.hitPointBonus = raceData.hitPointBonus;
      } else if (prev.hitPointBonus && prevRaceData?.hitPointBonus) {
        // Tear down: remove if it came from previous race.
        updates.hitPointBonus = 0;
      }
      if (raceData.savingThrows && Object.keys(raceData.savingThrows).length > 0) {
        updates.racialSavingThrows = raceData.savingThrows;
      } else if (prev.racialSavingThrows) {
        updates.racialSavingThrows = {};
      }

      // Log the changes
      const changesLog = [];
      if (prev.speed !== updates.speed) changesLog.push(`Speed: ${prev.speed || '30'} → ${updates.speed}`);
      if (prev.size !== updates.size) changesLog.push(`Size: ${prev.size || 'Medium'} → ${updates.size}`);
      if (prev.darkvision !== updates.darkvision) changesLog.push(`Darkvision: ${prev.darkvision || '0'} → ${updates.darkvision} ft`);
      if ((updates.languages || []).length !== (prev.languages || []).length) {
        const added = (updates.languages || []).filter(l => !(prev.languages || []).includes(l));
        const removed = (prev.languages || []).filter(l => !(updates.languages || []).includes(l));
        if (added.length || removed.length) {
          changesLog.push(`Languages: ${added.length ? `+${added.join(', ')}` : ''}${removed.length ? ` -${removed.join(', ')}` : ''}`.trim());
        }
      }
      if ((updates.damageResistance || []).length !== (prev.damageResistance || []).length) {
        changesLog.push(`Resistances: ${(updates.damageResistance || []).join(', ') || '(none)'}`);
      }

      if (changesLog.length > 0 && logActivity) {
        logActivity(
          `Race changed to ${currentRace}${currentSubrace ? ` (${currentSubrace})` : ''}`,
          'character',
          {
            race: currentRace,
            subrace: currentSubrace || 'None',
            changes: changesLog,
            autoUpdated: ['speed', 'size', 'darkvision', 'languages', 'resistances', 'features', 'proficiencies']
          }
        );
      }

      return {
        ...prev,
        ...updates
      };
    });

    // [ARC-012 FIX]: Emit event so other systems know race changed
    systemBus.emit(EVENTS.RACE_CHANGED, {
      oldRace: previousRaceRef.current,
      newRace: currentRace,
      newSubrace: currentSubrace,
      source: 'useRaceEffects'
    });

    // Update refs for next comparison
    previousRaceRef.current = currentRace;
    previousSubraceRef.current = currentSubrace;

  }, [character?.basics?.race, character?.basics?.subrace, character?.race, character?.subrace, setCharacter, logActivity]);
};

export default useRaceEffects;
